import { API_BASE } from "../../../lib/api.ts";
/**
 * Client for the AIS Shared Signals admin API (G3 #139): outbound streams, the outbox health view and
 * the inbound receiver. Each write goes through the BFF's service-admin proxy, which enforces the admin
 * role, a reason and a strict step-up before anything reaches the AIS. The AIS answers `{error:"code"}`
 * strings while the BFF answers `{error:{code}}`, so this client reads both and returns the code.
 */
export const SIGNALS_EVENT_TYPES = ["agent-revoked", "agent-suspended", "agent-reinstated", "host-changed"] as const;
export type SignalsEventType = (typeof SIGNALS_EVENT_TYPES)[number];
export const SIGNALS_EVENT_LABEL: Readonly<Record<SignalsEventType, string>> = {
  "agent-revoked": "Agent revoked",
  "agent-suspended": "Agent suspended",
  "agent-reinstated": "Agent reinstated",
  "host-changed": "Host changed",
};
export interface SignalsStream {
  readonly stream_id: string;
  readonly receiver_url: string;
  readonly receiver_host: string | null;
  readonly audience: string;
  readonly event_types: readonly string[];
  readonly include_standard_events: boolean;
  readonly status: "enabled" | "paused" | "paused_by_failure";
  readonly consecutive_dead: number;
  readonly version: number;
}
export interface StreamsResponse {
  readonly streams: readonly SignalsStream[];
  readonly transmitter_enabled: boolean;
  readonly receiver_guidance: { readonly accept_iat_max_age_s: number; readonly note: string };
}
export interface OutboxRow {
  readonly outboxId: string;
  readonly streamId: string;
  readonly eventType: string;
  readonly status: "pending" | "delivered" | "failed" | "dead";
  readonly attempts: number;
  readonly lastStatus: number | null;
  readonly lastError: string | null;
  readonly createdAt: number;
  readonly finishedAt: number | null;
}
export interface OutboxResponse {
  readonly counts: ReadonlyArray<{ readonly status: string; readonly streamId: string; readonly count: number }>;
  readonly recent: readonly OutboxRow[];
  readonly transmitter_enabled: boolean;
}
export interface ReceiverView {
  readonly stream_id: string;
  readonly receive_url: string;
  readonly issuer: string;
  readonly jwks_uri: string;
  readonly audience: string;
  readonly allowed_event_types: readonly string[];
  readonly principal_issuers: readonly string[];
  readonly max_age_s: number;
  readonly enabled: boolean;
  readonly jwks_other_host: boolean;
  readonly bearer_configured: boolean;
}
export interface ReceiverResponse {
  readonly receiver: ReceiverView | null;
  readonly receiver_enabled: boolean;
}
export const STREAMS_PATH = "ssf/streams";
export const OUTBOX_PATH = "ssf/outbox";
export const RECEIVER_PATH = "ssf/receiver";
export const SIGNALS_QUERY_ROOT = ["service-admin", "ais"] as const;
export type SignalsResult<T> =
  | { readonly ok: true; readonly body: T }
  | { readonly ok: false; readonly status: number; readonly code: string };
function codeOf(parsed: unknown, status: number): string {
  if (typeof parsed === "object" && parsed !== null) {
    const error = (parsed as { error?: unknown }).error;
    if (typeof error === "string") return error;
    if (typeof error === "object" && error !== null && typeof (error as { code?: unknown }).code === "string") {
      return (error as { code: string }).code;
    }
  }
  return `http_${status}`;
}
/** Send one signals write. Never throws for an HTTP answer; a network failure resolves `network`. */
export async function signalsWrite<T>(
  method: "POST" | "PATCH" | "DELETE" | "PUT",
  subpath: string,
  body: Readonly<Record<string, unknown>>,
): Promise<SignalsResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/service-admin/ais/${subpath}`, {
      method,
      headers: { accept: "application/json", "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
  } catch {
    return { ok: false, status: 0, code: "network" };
  }
  const parsed: unknown = await response.json().catch(() => null);
  if (response.ok) return { ok: true, body: parsed as T };
  return { ok: false, status: response.status, code: codeOf(parsed, response.status) };
}
export function streamPath(streamId: string): string {
  return `${STREAMS_PATH}/${encodeURIComponent(streamId)}`;
}
/** Plain-language text for a refusal code. Anything unknown gets a generic line, never a raw code. */
export function signalsMessage(code: string): string {
  switch (code) {
    case "mfa_required":
      return "Enter a current two-factor code to continue.";
    case "mfa_enrollment_required":
      return "Set up two-factor authentication on your account first. This action needs a second factor.";
    case "invalid_request":
      return "Check the fields and add a reason of 1 to 256 characters.";
    case "invalid_audience":
      return "That audience is not allowed. Choose an identifier of your own; it cannot be this service's address or any audience it issues or accepts.";
    case "invalid_receiver_url":
      return "The receiver URL must be https on port 443, with a public host name and no credentials.";
    case "ssrf_blocked":
      return "That host is not allowed. Use a public host name; internal names and IP addresses are refused.";
    case "dns_failed":
      return "The receiver host name could not be resolved. Check the name and try again.";
    case "invalid_event_types":
      return "Choose at least one event type.";
    case "jwks_host_mismatch":
      return "The key set must live on the issuer's host, or a parent or child of it. Tick the override only if you trust the other host.";
    case "stream_limit":
      return "This team already has the maximum number of streams. Delete one first.";
    case "forbidden":
      return "Only a team administrator can do this.";
    case "version_conflict":
      return "The stream changed while this dialog was open. Close it and review the current state.";
    case "tenant_required":
    case "not_found":
      return "That item is not in your team.";
    case "service_auth_failed":
      return "The two-factor confirmation could not be passed to the AIS. Try again with a fresh code.";
    case "network":
      return "The request could not be sent. Check your connection and try again.";
    default:
      return "The action could not be completed. Nothing was changed.";
  }
}
